import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaWhatsapp, FaCheckCircle, FaTractor, FaBolt, FaChartLine,
  FaTools, FaCloud, FaCoins, FaSeedling, FaCheck, FaMap, FaBars, FaTimes,
  FaArrowRight, FaArrowLeft, FaChevronDown,
  FaDatabase, FaMicrochip, FaFlask, FaTachometerAlt, FaGasPump,
  FaGithub,
} from 'react-icons/fa';
import '../styles/landingPage.scss';
import bootcamp   from '../assets/bootcamp.png';
import dynasystem from '../assets/portfolio_tractorlab_bg.png';
import dynaop     from '../assets/portfolio_bg.png';
import dynamap    from '../assets/ksatwebgis.png';
import heroDiagnostico from '../assets/hero_bg.png';
import playstoreIcon from '../assets/playstore.jpeg';
import apkIcon     from '../assets/apk.png';

import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

const GOOGLE_FORM_ACTION_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSciBwLutHhanbjJ2E_MaTn_r9f4TD8-gSktWXsZuTiyMlF84A/formResponse';

const FORM_FIELD_NAMES = {
  name:  'entry.986590372',
  email: 'entry.1278167896',
  phone: 'entry.12986911',
};

const WHATSAPP_NUMBER = '5519995910565';

const LANGUAGES = [
  { code: 'pt', flag: '🇧🇷', label: 'Português', href: '/' },
  { code: 'en', flag: '🇺🇸', label: 'English',    href: '/en' },
  { code: 'es', flag: '🇪🇸', label: 'Español',    href: '/es' },
];

const NAV_ITEMS = [
  { id: 'hero',      label: 'Home' },
  { id: 'problem',   label: 'Problem' },
  { id: 'portfolio', label: 'Solutions' },
  { id: 'services',  label: 'Diagnosis' },
  { id: 'about',     label: 'Startup' },
  { id: 'blog',      label: 'Blog', href: '/en/blog' },
];

const SLIDES = [
  {
    id: 1,
    tag: 'Agricultural Sustainability',
    tagDot: '#A8FF3E',
    title: ['Every farming operation generates data.', 'We turn', 'that data into profit.'],
    accentLine: 1,
    accentColor: '#A8FF3E',
    subtitle: 'Discover our line of digital agriculture solutions and see how we identify savings opportunities in your farm production costs.',
    cta: { label: 'Explore Solutions', wa: "I'd like to learn how the startup's solutions can optimize my farm's costs." },
    badge: 'Farming Efficiency',
    image: heroDiagnostico,
  },
];

const PRODUCTS = [
  {
    id: 'dynaop',
    status: 'In R&D', statusType: 'coming',
    tag: 'Deep Tech · Embedded IoT',
    name: 'Real-Time Diesel Optimization (Agricultural Tractors)',
    description: 'An embedded system that combines diesel engine dynamometer curves with real-time optimization algorithms. The operator gets continuous guidance to keep the tractor at its point of maximum thermal efficiency, cutting fuel use without losing productivity.',
    specs: [
      { icon: <FaMicrochip />, label: 'Hardware + Software' },
      { icon: <FaBolt />,      label: 'Estimated savings of up to 30% per tractor' },
      { icon: <FaDatabase />,  label: 'Real-time data' },
    ],
    image: dynaop,
    action: null,
    accent: '#4A8A28',
  },
  {
    id: 'dynasystem',
    status: 'Available', statusType: 'live',
    tag: 'Engineering · Technical Calculator',
    name: 'Traction Efficiency for Tractor-Implement Systems',
    description: 'A traction and ballast calculator built on agricultural engineering models. It sizes the required drawbar pull, calculates ballast distribution, and identifies the optimal wheel-slip point for any tractor-implement combination.',
    specs: [
      { icon: <FaTools />,   label: 'Traction + Ballast Modules' },
      { icon: <FaTractor />, label: 'Performance Charts & Estimated Savings' },
      { icon: <FaFlask />,   label: 'Based on ASABE standards' },
    ],
    image: dynasystem,
    storeBadges: [
      { type: 'playstore', status: 'coming',    label: 'Google Play', sub: 'Coming soon', icon: playstoreIcon },
      { type: 'apk',       status: 'available', label: 'Android APK', sub: 'Available',   icon: apkIcon, href: 'https://github.com/seu-usuario/dynasystem' },
    ],
    repoLink: 'https://github.com/seu-usuario/dynasystem',
    accent: '#8A6A00',
    extraAction: {
      label: 'Technical Consulting',
      wa: "I'd like a quote and more information about the soil-tractor-implement optimization consulting.",
      icon: <FaWhatsapp />,
    },
    accent: '#181818',
  },
  {
    id: 'dynamap',
    status: 'Available', statusType: 'live',
    tag: 'Soil Physics · Water Dynamics · WebGIS Platform',
    name: 'Infiltration Maps and Compaction Zones',
    description: 'High-resolution hydro-physical diagnostics. We map saturated hydraulic conductivity (Ksat) to identify management zones based on the soil profile\'s actual infiltration, drainage, and water-retention capacity.',
    specs: [
      { icon: <FaMap />,       label: 'Machine learning prediction' },
      { icon: <FaFlask />,     label: 'Attribute: soil physics' },
      { icon: <FaChartLine />, label: 'Management zone generation' },
      { icon: <FaTools />, label: 'WebGIS platform' },
    ],
    image: dynamap,
    action: { href: 'https://soilwater.vercel.app', label: 'Access platform', icon: <FaArrowRight /> },
    extraAction: {
      label: 'Request WebGIS Quote',
      wa: "I'd like a quote for implementing the WebGIS for infiltration and compaction mapping.",
      icon: <FaWhatsapp />,
    },
    accent: '#181818',
  },
];

const PROBLEMS = [
  {
    num: '01', icon: <FaBolt />, accent: '#4A8A28',
    title: 'Excessive Energy Consumption',
    text: 'Tractors running outside the optimal torque point burn up to 30% more fuel. Matching gear and engine speed eliminates that waste.',
  },
  {
    num: '02', icon: <FaTools />, accent: '#8A6A00',
    title: 'Tractor-Implement Mismatch',
    text: 'Wheel slip above 15% or below 8% signals a poorly sized setup — leading to soil compaction and lower productivity.',
  },
  {
    num: '03', icon: <FaMap />, accent: '#006080',
    title: 'Hydraulic Conductivity Mapping',
    text: 'Ignoring soil variability undermines water management. We map saturated conductivity to define precise management zones.',
  },
  {
    num: '04', icon: <FaChartLine />, accent: '#B23A00',
    title: 'Decisions Without Data',
    text: 'Without continuous monitoring, adjustments come down to guesswork. Real-time data turns intuition into precision.',
  },
];

const SERVICES = [
  {
    ods: 'SDG 9',
    title: 'Efficiency in Farm Management',
    text: 'Real-time CAN bus telemetry and proper tractor-implement sizing cut idle time and raise operational productivity.',
    icon: <FaTractor />,
    cta: 'Read more',
    wa: "I want to optimize my fleet's farm management efficiency",
    variant: 'primary',
    accent: '#6BBF3C',
  },
  {
    ods: 'SDG 13',
    title: 'CO₂ Emission Reduction',
    text: 'Fuel consumption monitoring (Cappelli model) identifies losses and cuts emissions per hectare farmed.',
    icon: <FaCloud />,
    cta: 'Read more',
    wa: "I want to reduce my operation's CO2 emissions",
    variant: 'default',
    accent: '#4A90D9',
  },
  {
    ods: 'SDG 8',
    title: 'Lower Operating Costs',
    text: 'Correct sizing (Brixius/Godwin-Reece) prevents wheel slip, premature wear, and excess diesel consumption.',
    icon: <FaCoins />,
    cta: 'Read more',
    wa: "I want to lower my fleet's operating costs",
    variant: 'default',
    accent: '#D9A441',
  },
  {
    ods: 'SDG 2',
    title: 'Food Security',
    text: 'Data-driven decisions increase crop predictability, supporting food production at scale.',
    icon: <FaSeedling />,
    cta: 'Read more',
    wa: "I want to strengthen my production's food security",
    variant: 'primary',
    accent: '#3CA65E',
  },
];

const FORM_FIELDS = [
  { id: 'name',  label: 'Full Name', type: 'text',  placeholder: 'John Smith',       autoComplete: 'name' },
  { id: 'email', label: 'Email',     type: 'email', placeholder: 'john@farm.com',    autoComplete: 'email' },
  { id: 'phone', label: 'WhatsApp',  type: 'tel',   placeholder: '(+1) 555-123-4567', autoComplete: 'tel' },
];

const SLIDE_INTERVAL_MS = 6000;

const LandingPageEn = () => {
  const [activeSection, setActiveSection]   = useState('hero');
  const [isMenuOpen, setIsMenuOpen]         = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [currentSlide, setCurrentSlide]     = useState(0);
  const [slideDirection, setSlideDirection] = useState('next');
  const [isSlideAnimating, setIsSlideAnimating] = useState(false);
  const [formData, setFormData]             = useState({ name: '', email: '', phone: '' });
  const [formStatus, setFormStatus]         = useState({ loading: false, success: false, error: false });
  const slideTimerRef = useRef(null);

  const sectionRefs = {
    hero:        useRef(null),
    problem:     useRef(null),
    portfolio:   useRef(null),
    services:    useRef(null),
    about:       useRef(null),
    leadCapture: useRef(null),
  };

  const hasMultipleSlides = SLIDES.length > 1;

  useEffect(() => {
    const onScroll = () => setIsHeaderScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const goToSlide = useCallback((index, direction = 'next') => {
    if (isSlideAnimating || index === currentSlide) return;
    setSlideDirection(direction);
    setIsSlideAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsSlideAnimating(false);
    }, 600);
  }, [isSlideAnimating, currentSlide]);

  const nextSlide = useCallback(
    () => goToSlide((currentSlide + 1) % SLIDES.length, 'next'),
    [currentSlide, goToSlide]
  );
  const prevSlide = useCallback(
    () => goToSlide((currentSlide - 1 + SLIDES.length) % SLIDES.length, 'prev'),
    [currentSlide, goToSlide]
  );

  const resetTimer = useCallback(() => {
    clearInterval(slideTimerRef.current);
    if (hasMultipleSlides) {
      slideTimerRef.current = setInterval(nextSlide, SLIDE_INTERVAL_MS);
    }
  }, [nextSlide, hasMultipleSlides]);

  useEffect(() => {
    if (!hasMultipleSlides) return undefined;
    slideTimerRef.current = setInterval(nextSlide, SLIDE_INTERVAL_MS);
    return () => clearInterval(slideTimerRef.current);
  }, [nextSlide, hasMultipleSlides]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 100;
      for (const [section, ref] of Object.entries(sectionRefs)) {
        if (ref.current && ref.current.offsetTop <= scrollPos) {
          setActiveSection(section);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    sectionRefs[id]?.current?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const openWhatsApp = (message) =>
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');

  const handleFormChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: false, error: false });

    const body = new URLSearchParams();
    body.append(FORM_FIELD_NAMES.name,  formData.name);
    body.append(FORM_FIELD_NAMES.email, formData.email);
    body.append(FORM_FIELD_NAMES.phone, formData.phone);

    try {
      await fetch(GOOGLE_FORM_ACTION_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFormStatus({ loading: false, success: true, error: false });
      setFormData({ name: '', email: '', phone: '' });
    } catch {
      setFormStatus({ loading: false, success: false, error: true });
    }
  };

  const slide = SLIDES[currentSlide];

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const msg = [
      `*DynaTech Diagnosis*`,
      `Name: ${data.get('name')}`,
      `Email: ${data.get('email')}`,
      `WhatsApp: ${data.get('whatsapp')}`,
      `Farm: ${data.get('farm')}`,
      `Total area: ${data.get('area')} ha`,
      `Main interest: ${data.get('interest')}`,
    ].join('\n');
    openWhatsApp(msg);
  };

  return (
    <div className="lp">

      <header className={`lp__header ${isHeaderScrolled || isMenuOpen ? 'lp__header--scrolled' : ''}`}>
        <div className="lp__header-inner">
          <a className="lp__logo" href="/en">
            <FaTractor aria-hidden="true" /><span>DYNA<em>TECH</em></span>
          </a>

          <nav className={`lp__nav ${isMenuOpen ? 'lp__nav--open' : ''}`} aria-label="Main navigation">
            {NAV_ITEMS.map(({ id, label, href }) => (
              href ? (
                <a key={id} href={href} className="lp__nav-link">{label}</a>
              ) : (
                <button
                  key={id}
                  type="button"
                  className={`lp__nav-link ${activeSection === id ? 'lp__nav-link--active' : ''}`}
                  onClick={() => scrollToSection(id)}
                >
                  {label}
                </button>
              )
            ))}

            <div className="lp__lang-switcher">
              {LANGUAGES.map(({ code, flag, label, href }) => (
                <button
                  key={code}
                  type="button"
                  title={label}
                  aria-label={label}
                  onClick={() => { window.location.href = href; }}
                >
                  {flag}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="lp__nav-cta"
              onClick={() => openWhatsApp('I would like to learn how DynaTech can optimize my operation.')}
            >
              <FaWhatsapp aria-hidden="true" /> Contact Us
            </button>
          </nav>

          <button
            type="button"
            className="lp__menu-btn"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      <section className="lp__hero" ref={sectionRefs.hero}>
        <div
          className={`lp__hero-bg ${isSlideAnimating ? `lp__hero-bg--${slideDirection}` : ''}`}
          style={{ backgroundImage: `url(${slide.image})` }}
          role="img"
          aria-label={slide.badge}
        />
        <div className="lp__hero-overlay" />

        <div className={`lp__hero-content ${isSlideAnimating ? 'lp__hero-content--exit' : 'lp__hero-content--enter'}`}>
          <h1 className="lp__hero-title">
            {slide.title.map((line, i) => (
              <span key={line} style={{ animationDelay: `${i * 0.08}s` }}>
                {i === slide.accentLine ? <em style={{ color: slide.accentColor }}>{line}</em> : line}
              </span>
            ))}
          </h1>

          <p className="lp__hero-sub">{slide.subtitle}</p>

          <div className="lp__hero-actions">
            <button
              type="button"
              className="lp__btn lp__btn--primary"
              style={{ background: slide.accentColor, color: '#0A1A0B' }}
              onClick={() => openWhatsApp(slide.cta.wa)}
            >
              <FaWhatsapp aria-hidden="true" /> {slide.cta.label}
            </button>
            <button type="button" className="lp__btn lp__btn--outline-light" onClick={() => scrollToSection('portfolio')}>
              View Solutions <FaArrowRight aria-hidden="true" />
            </button>
          </div>

          <div className="lp__hero-badge">
            <span style={{ color: slide.accentColor }}>✦</span> {slide.badge}
          </div>
        </div>

        {hasMultipleSlides && (
          <>
            <div className="lp__carousel-controls">
              <button
                type="button"
                className="lp__carousel-arrow"
                aria-label="Previous slide"
                onClick={() => { prevSlide(); resetTimer(); }}
              >
                <FaArrowLeft />
              </button>
              <div className="lp__carousel-dots">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    className={`lp__carousel-dot ${i === currentSlide ? 'lp__carousel-dot--active' : ''}`}
                    style={i === currentSlide ? { background: slide.accentColor } : undefined}
                    onClick={() => { goToSlide(i, i > currentSlide ? 'next' : 'prev'); resetTimer(); }}
                  />
                ))}
              </div>
              <button
                type="button"
                className="lp__carousel-arrow"
                aria-label="Next slide"
                onClick={() => { nextSlide(); resetTimer(); }}
              >
                <FaArrowRight />
              </button>
            </div>

            <div className="lp__carousel-counter">
              <span style={{ color: slide.accentColor }}>{String(currentSlide + 1).padStart(2, '0')}</span>
              <span className="lp__carousel-counter-sep" />
              <span>{String(SLIDES.length).padStart(2, '0')}</span>
            </div>
          </>
        )}

        <button type="button" className="lp__scroll-hint" aria-label="Scroll to next section" onClick={() => scrollToSection('problem')}>
          <FaChevronDown />
        </button>
      </section>

      <section className="lp__problem" ref={sectionRefs.problem}>
        <div className="lp__container">
          <div className="lp__problem-intro">
            <div>
              <h2 className="lp__section-title">
                The invisible inefficiency<br />
                <span>eating into your profit</span>
              </h2>
            </div>
            <p className="lp__section-sub">
              Every farming operation carries silent losses. Identifying them is the first step toward turning cost into profitability.
            </p>
          </div>

          <div className="lp__problem-grid">
            {PROBLEMS.map((card) => (
              <div key={card.num} className="lp__problem-card" style={{ '--card-accent-color': card.accent }}>
                <span className="lp__problem-icon" style={{ color: card.accent }}>{card.icon}</span>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp__portfolio" ref={sectionRefs.portfolio}>
        <div className="lp__container">
          {/* <h2 className='h2-portfolio'>Our Precision Agriculture Portfolio</h2> */}
          <div className="lp__portfolio-layout">
            <div className="lp__portfolio-intro">
              <div className="lp__eyebrow">The DynaTech Platform</div>
              <h2 className="lp__section-title">
                Digital solutions for smarter decisions and <span>lower costs</span>
              </h2>
              <p className="lp__section-sub">
                A complete platform that collects, analyzes, and turns data into intelligence to help you make better decisions.
              </p>
              <button type="button" className="lp__btn lp__btn--primary" onClick={() => scrollToSection('portfolio')}>
                View platform <FaArrowRight aria-hidden="true" />
              </button>
            </div>

            <div className="lp__portfolio-grid">
              {PRODUCTS.map((product) => (
                <article key={product.id} className="lp__product-card">
                  <div className="lp__product-card-image">
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div className="lp__product-card-body">
                    <h3>{product.name}</h3>
                    <p>{product.description}</p>

                    <ul className="lp__product-card-checklist">
                      {product.specs.map((spec) => (
                        <li key={spec.label}>
                          <FaCheck style={{ color: product.accent }} aria-hidden="true" />
                          {spec.label}
                        </li>
                      ))}
                    </ul>

                    {product.storeBadges && (
                      <div className="lp__product-stores">
                        {product.storeBadges.map((badge) => (
                          <div key={badge.type} className={`lp__store-badge lp__store-badge--${badge.status}`}>
                            <img src={badge.icon} alt="" loading="lazy" decoding="async" />
                            <div className="lp__store-badge-text">
                              <span className="lp__store-badge-label">{badge.sub}</span>
                              <strong>{badge.label}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {product.action && (
                      <a
                        href={product.action.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp__btn lp__btn--product"
                        style={{ background: product.accent, color: '#fff' }}
                      >
                        {product.action.label} {product.action.icon}
                      </a>
                    )}

                    {product.extraAction && (
                      <button
                        type="button"
                        className="lp__btn lp__btn--product"
                        style={{ background: '#F5A623', color: '#1A1A1A' }}
                        onClick={() => openWhatsApp(product.extraAction.wa)}
                      >
                        {product.extraAction.icon} {product.extraAction.label}
                      </button>
                    )}

                    {!product.action && !product.storeBadges && !product.repoLink && !product.extraAction && (
                      <button type="button" className="lp__btn lp__btn--soon" disabled>
                        <span className="lp__btn-soon-dot" /> Coming soon
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lp__results">
        <div className="lp__container">
          <div className="lp__results-inner">
            <div className="lp__results-text">
              <div className="lp__eyebrow">Results</div>
              <h2 className="lp__section-title">
                Measurable impact <br /><span>in every operation.</span>
              </h2>
              <p className="lp__section-sub">
                Data that turns your farm management into precise, profitable decisions.
              </p>
            </div>

            <div className="lp__results-cards">
              <div className="lp__result-card">
                <span className="lp__result-icon"><FaTachometerAlt /></span>
                <h3>Specific Fuel Consumption Monitoring</h3>
              </div>
              <div className="lp__result-card">
                <span className="lp__result-icon"><FaGasPump /></span>
                <h3>Liters/Hour Saved</h3>
              </div>
              <div className="lp__result-card">
                <span className="lp__result-icon"><FaSeedling /></span>
                <h3>Improved Soil Management</h3>
              </div>
              <div className="lp__result-card">
                <span className="lp__result-icon"><FaChartLine /></span>
                <h3>Data-Driven Decisions</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lp__services" ref={sectionRefs.services}>
        <div className="lp__container">
          <div className="lp__services-header">
            <div className="lp__eyebrow lp__eyebrow--light">High-Impact Diagnosis</div>
            <h2 className="lp__section-title lp__section-title--light">
              4 Pillars of Optimization<br />
              <em>for Every Operation</em>
            </h2>
            <p className="lp__section-sub lp__section-sub--light">
              A structured agricultural engineering methodology that turns field data into concrete operational improvements.
            </p>
          </div>

          <div className="lp__services-grid">
            {SERVICES.map((svc) => (
              <div key={svc.title} className={`lp__service-card lp__service-card--${svc.variant}`}>
                <span className="lp__service-ods">{svc.ods}</span>
                <span className="lp__service-icon" style={{ color: svc.accent }}>{svc.icon}</span>
                <h3 className="lp__service-title">{svc.title}</h3>
                <p className="lp__service-text">{svc.text}</p>
                <button type="button" className="lp__service-cta" onClick={() => openWhatsApp(svc.wa)}>
                  {svc.cta} <FaArrowRight aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp__about" ref={sectionRefs.about}>
        <div className="lp__container">
          <div className="lp__about-grid">
            <div className="lp__about-image">
              <div className="lp__about-main-img">
                <img
                  src={bootcamp}
                  alt="DynaTech team during the Sebrae for Startups bootcamp"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            <div className="lp__about-content">
              <div className="lp__eyebrow">The Startup</div>
              <h2 className="lp__section-title">
                DynaTech – AgTech Ecosystem<br />
                <span>Built on Scientific Rigor</span>
              </h2>
              <p>
                DynaTech was born at the intersection of academic research in agricultural mechanization,
                machine learning, soil physics, and frontier technology development. Through software engineering, we combine deep domain knowledge of agricultural problems with embedded systems and applications (mobile and web), covering the entire product development cycle for digital agriculture.
              </p>

              <div className="lp__about-badges">
                <div className="lp__about-badge">
                  <div>
                    <strong>André Luiz Bandeli Júnior (Founder)</strong>
                    <span>Agricultural Engineering student, Unicamp | Mechatronics Technician, COTUCA/UNICAMP | Software Engineering | LinkedIn: <a href="linkedin.com/in/andrebandeli/">/andrebandeli</a> | Research: <a href="https://www.researchgate.net/profile/Andre-Bandeli-Junior">Researchgate</a></span>
                    <br /><span>Campinas, São Paulo, Brazil.</span>
                  </div>
                </div>
                <div className="lp__about-badge">
                  <div>
                    <strong>Sebrae for Startups | IPT | ABStartups</strong>
                    <span>Business Model Validation | Finalists — Spark 2025 | Deep Tech Classification | Frontier Innovation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className="lp__cta-strip" id="diagnostico">
        <div className="lp__container">
          <h2>Don't let losses become<br /><span>your next fixed cost.</span></h2>
          <p>Find out how much you can save with our agricultural optimization solution.</p>

          <div className="wg-lead__form lp__cta-strip-form">
            <form onSubmit={handleLeadSubmit}>

              <div className="wg-lead__form-row">
                <input name="name"  type="text"  placeholder="Full name" required />
                <input name="email" type="email" placeholder="Email"     required />
              </div>

              <div className="wg-lead__form-row">
                <input name="whatsapp" type="tel"  placeholder="WhatsApp (with country code)" required />
                <input name="farm"     type="text" placeholder="Farm / municipality"           required />
              </div>

              <div className="wg-lead__form-row">
                <input name="area" type="number" placeholder="Total area (ha)" required min="1" />
                <select name="interest" required>
                  <option value="">I'm interested in...</option>
                  <option value="Diesel optimization (Dyna Operator)">Diesel optimization</option>
                  <option value="Tractor-implement traction efficiency">Tractor-implement traction</option>
                  <option value="Soil mapping (WebGIS)">Soil mapping (WebGIS)</option>
                  <option value="Not sure yet, I want an assessment">Not sure yet, want an assessment</option>
                </select>
              </div>

              <button type="submit" className="wg-btn wg-btn--primary wg-btn--block">
                <FaWhatsapp /> Send and talk to a specialist
              </button>

            </form>
          </div>
        </div>
      </section>

      <footer className="lp__footer">
        <div className="lp__container">
          <div className="lp__footer-grid">
            <div className="lp__footer-brand">
              <a className="lp__logo" href="/en"><FaTractor aria-hidden="true" /><span>DYNA<em>TECH</em></span></a>
              <p>Science-based agricultural optimization. From the field to the decision.</p>
            </div>

            <div className="lp__footer-col">
              <h4>Solutions</h4>
              <a href="/en/blog/dynaoperator">Dyna Operator</a>
              <a href="https://dynasystem.onrender.com/" target="_blank" rel="noopener noreferrer">Dyna System</a>
              <a href="/en/blog/dynamap">Dyna Map</a>
            </div>

            <div className="lp__footer-col">
              <h4>Company</h4>
              <a href="/en/about">About Us</a>
              <a href="/en/blog">DynaTech Blog</a>
            </div>

            <div className="lp__footer-col">
              <h4>Contact</h4>
              <button
                type="button"
                className="lp__footer-col-link-btn"
                onClick={() => openWhatsApp('Hello, I would like more information about DynaTech.')}
              >
                <FaWhatsapp aria-hidden="true" /> WhatsApp
              </button>
              <a href="">LinkedIn</a>
              <a href="mailto:agritech.dyna@gmail.com">agritech.dyna@gmail.com</a>

            </div>
          </div>

          <div className="lp__footer-bottom">
            <p>© 2026 DynaTech · Agricultural Optimization AgTech · All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageEn;